import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { Box, Link } from '@mui/material';
import { useAuth } from '@/contexts/AuthContext';
import { styled } from '@mui/material/styles';
import { Button, Typography } from '@/components/ui';
import { toast } from 'react-hot-toast';
import { useAppDispatch } from '@/store/redux';
import { activateAccount } from '@/store/redux/slices/authSlice';

// Styled components matching Regions design
const LoginContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  height: '100%',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
  },
}));

const LeftPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 40%',
  padding: theme.spacing(2, 4),
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  backgroundColor: 'white',
  [theme.breakpoints.down('md')]: {
    flex: '1',
    padding: theme.spacing(4, 3),
  },
  paddingBottom: '100px',
}));

const RightPanel = styled(Box)(({ theme }) => ({
  flex: '0 0 50%',
  position: 'relative',
  overflow: 'hidden',
  [theme.breakpoints.down('md')]: {
    flex: '0 0 200px',
  },
}));

// Login Banner styled components
const BannerContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  //background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
}));

const BannerImage = styled('img')(() => ({
  width: '100%',
  height: '100%',
  objectPosition: 'cover',
}));

const BrandHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(4),
  gap: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    textAlign: 'center',
  },
}));

const WelcomeSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(1),
}));

const FormSection = styled(Box)(() => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
}));

export const AccountActivationForm: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  const { loading } = useAuth();
  const dispatch = useAppDispatch();

  let tokenParam = searchParams.get('token');

  const activateAccountWithToken = async () => {
    if (!tokenParam) {
      setError('No activation token provided');
      return;
    }

    const loadingToast = toast.loading(
      'Welcome back! Getting everything ready for you…',
      {
        position: 'top-center',
        icon: (
          <img
            src="https://upload.wikimedia.org/wikipedia/commons/c/c7/Loading_2.gif?20170503175831"
            alt="loading"
            style={{
              width: '50px',   // change size here
              height: '50px',
              objectFit: 'contain',
              marginRight: '8px'
            }}
          />
        ),
        style: {
          marginTop: '40vh',
          transform: 'translateY(-50%)',

          background: '#ffffff',
          color: '#000',
          padding: '18px 24px',
          borderRadius: '12px',
          width: '620px',
          textAlign: 'center',

          borderTop: '4px solid #17A6DA',
          fontSize: '18px',
          fontWeight: 500,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
        },
      }
    );

    try {
      setError(null); // Clear any previous errors
      const response = await dispatch(activateAccount(tokenParam));
      toast.dismiss(loadingToast);
      // Handle the response based on the API structure
      const result = response?.payload || response;
      if (result?.data?.success === true) {
        // Handle successful activation - toast is handled by Redux slice
        setTimeout(() => {
          navigate('/auth/login', {
            replace: true,
          });
        }, 3000);
      }
    } catch (err: any) {
      // Handle network errors or exceptions
      const errorMessage = err?.response?.data?.message ||
        err?.message ||
        'Activation link has already been used. Please try again or contact support.';
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    if (!tokenParam) {
      setError('No activation token provided. Redirecting to login page...');
      setTimeout(() => {
        navigate('/auth/register');
      }, 3000);
      return;
    }
  }, [searchParams, navigate]);


  const LogoHeader = styled(Box)(({ theme }) => ({
    position: 'absolute',
    top: theme.spacing(3),
    left: theme.spacing(4),
    // [theme.breakpoints.down('sm')]: {
    //   left: '50%',
    //   transform: 'translateX(-50%)',
    // },
  }));

  return (
    <LoginContainer>
      <LogoHeader>
        <img
          src="/logos/logo-white_1.png"
          alt="GrowthBnk Logo"
          style={{ width: '150px', objectFit: 'contain' }}
        />
      </LogoHeader>
      {/* Left Panel - Login Form */}
      <LeftPanel>

        <FormSection>
          {/* Welcome Section - Team Login Style */}
          <WelcomeSection>
            <Typography
              variant="h1"
              weight="bold"
              color="textPrimary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '1.00rem', sm: '1.5rem' }, color: 'rgb(40, 42, 48)', pb: 4 }}
            >
              Welcome !
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign={'center'}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.75rem' }, pb: 4 }}
            >
              We're excited to have you get started. First, you need to confirm your account. Just
              press the button below.
            </Typography>
          </WelcomeSection>

          {/* Login Form */}
          <Box
            sx={{
              width: {
                xs: '320px', // Mobile devices
                sm: '420px', // Tablets
                md: '700px', // Desktops
                lg: '70%', // Large screens: full width
              },
              maxWidth: '100%',
              margin: '0 auto',
              pb: 4,
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {error && (
                <Typography color="error" align="center">
                  {error}
                </Typography>
              )}

              {/* Activation Button */}
              <Button
                type="submit"
                variant="primary"
                size="small"
                fullWidth
                loading={loading}
                loadingText="Activating account..."
                onClick={activateAccountWithToken}
                disabled={!tokenParam}
                sx={{
                  color: '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  borderRadius: '8px',
                  height: '50px',
                  '&:hover': {
                    backgroundColor: '#1392C1', // slightly darker
                  },
                  '&:disabled': {
                    backgroundColor: '#9bd3ea',
                    color: '#fff',
                  },
                }}

              >
                {tokenParam ? 'Confirm Account' : 'No Token Available'}
              </Button>
            </Box>
          </Box>

        </FormSection>
      </LeftPanel>

      {/* Right Panel - Login Banner */}
      <RightPanel>
        <BannerContainer>
          <BannerImage src="/banner.svg" alt="Team Login Banner" />
        </BannerContainer>
      </RightPanel>
    </LoginContainer>
  );
};

export default AccountActivationForm;
